import { Transition, Dialog, DialogPanel, DialogTitle, TransitionChild } from "@headlessui/react";
import classNames from "classnames";
import { Fragment, useRef } from "react";

type ConfirmModalProps = {
    id: string;
    headerText: string;
    cancelButtonText?: string;
    confirmButtonText: string;
    onCancel: () => void;
    onConfirm: () => void;
    onExit?: () => void;
    isOpen: boolean;
    confirmButtonColor: string;
    confirmHoverButtonColor: string;
    confirmDisabled?: boolean;
    ConfirmButtonIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    customZIndex?: number;
    contentText?: string;
    content?: React.JSX.Element;
    dontShowAgain?: boolean;
    setDontShowAgain?: (value: boolean) => void;
    modalWidth?: string;
    /** Where the dialog panel sits in the viewport; default centered. */
    placement?: "center" | "bottom-left";
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    id,
    headerText,
    cancelButtonText,
    confirmButtonText,
    onCancel,
    onConfirm,
    onExit,
    isOpen,
    contentText,
    content,
    confirmButtonColor,
    confirmHoverButtonColor,
    confirmDisabled = false,
    ConfirmButtonIcon,
    customZIndex,
    dontShowAgain,
    setDontShowAgain,
    modalWidth,
    placement = "center",
}) => {
    const isBottomLeft = placement === "bottom-left";
    const cancelButtonRef = useRef(null);
    const handleClose = (_e: { preventDefault: () => void }) => {
        onCancel();
    };

    const handleConfirm = (_e: { preventDefault: () => void }) => {
        onConfirm();
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog
                id={id}
                as="div"
                style={{
                    zIndex: customZIndex ?? 1000,
                    position: "relative",
                }}
                open={isOpen}
                onClose={onExit ? onExit : onCancel}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div
                    className={classNames(
                        "fixed flex inset-0 z-40 w-screen overflow-y-auto",
                        isBottomLeft ? "items-end justify-start" : "justify-center",
                    )}
                >
                    <div
                        className={classNames(
                            "flex w-full max-w-[500px] p-[24px]",
                            isBottomLeft
                                ? "items-end justify-start text-left pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))]"
                                : "min-h-full items-center justify-center text-center sm:items-center",
                        )}
                    >
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                className={classNames(
                                    "relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:p-6",
                                    modalWidth ? modalWidth : "max-w-[600px] md:min-w-[480px]",
                                )}
                            >
                                <div className=" text-left">
                                    <DialogTitle as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                                        {headerText}
                                    </DialogTitle>
                                </div>

                                {contentText && (
                                    <div className={content ? "mt-2" : "mt-6"}>
                                        <p className="text-[14px] text-gray-500">{contentText}</p>
                                    </div>
                                )}

                                {content && <div className="mt-[24px]">{content}</div>}

                                <div className="flex flex-row items-center mt-6">
                                    {dontShowAgain !== undefined && setDontShowAgain !== undefined && (
                                        <div className="flex flex-row w-full items-center justify-between">
                                            <div className="flex w-full flex-row items-center gap-1">
                                                <input
                                                    id="dontShowAgain"
                                                    name="dontShowAgain"
                                                    type="checkbox"
                                                    className="focus:ring-max-600 h-4 w-4 text-max-600 border-gray-300 rounded"
                                                    checked={dontShowAgain ? true : false}
                                                    onChange={(e) => setDontShowAgain(e.target.checked)}
                                                />
                                                <label
                                                    htmlFor="dontShowAgain"
                                                    className="ml-1 block text-sm text-gray-700 "
                                                >
                                                    Don't show this again
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex w-full justify-end gap-4">
                                        {cancelButtonText && (
                                            <button
                                                type="button"
                                                className="focused inline-flex justify-center rounded-md bg-white px-3 py-2 min-w-[60px] text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                                                onClick={handleClose}
                                                ref={cancelButtonRef}
                                            >
                                                {cancelButtonText}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={confirmDisabled}
                                            className={classNames(
                                                `focused inline-flex items-center justify-center rounded-md ${confirmButtonColor} px-3 py-2 min-w-[60px] text-sm font-semibold text-white shadow-sm hover:${confirmHoverButtonColor} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  sm:col-start-2`,
                                                confirmDisabled ? "opacity-50 cursor-not-allowed" : "",
                                            )}
                                            onClick={handleConfirm}
                                        >
                                            {ConfirmButtonIcon && <ConfirmButtonIcon className="h-4 w-4 mr-2" />}
                                            {confirmButtonText}
                                        </button>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default ConfirmModal;
